import { ButtonBlueprint, createButton, keygen, LayoutGrid, makeStyles, Typography } from '@archer-technologies/ui-components';
import { Getter, Plugin, Template, TemplateConnector } from '@devexpress/dx-react-core';
import { Table, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import TableCell from '@mui/material/TableCell';
import * as React from 'react';

export interface ActionPluginProps {
  actions: Action[];
  messages: Map<string, string>;
  translate: any;
}

export interface Action {
  id: ButtonBlueprint['id'];
  type: ButtonBlueprint['type'];
  actionType: ButtonBlueprint['actionType'];
  disabledIndex?: string;
  hiddenIndex?: string;
  onClick: (event: any) => void;
  tooltipTitle?: ButtonBlueprint['tooltipTitle'];
  toolTipPosition?: ButtonBlueprint['toolTipPosition'];
  dataProp?: string;
}

const useStyles = makeStyles(() => {
  return {
    cellStyle: {
      padding: '0 !important'
    },
    blankStyle: {
      visibility: 'hidden'
    }
  };
});

const pluginDependencies = [{ name: 'Table' }];
const buildActionToolbar = (actions: Action[], translate: ActionPluginProps['translate'], row: any, blankStyle?: any) => {
  const iconButtonTypes: string[] = actions.map(action => action.type);
  const actionsList =
    actions &&
    actions
      .filter(action => {
        return !action.hiddenIndex || !row[action.hiddenIndex]; // Remove all items that need to be hidden
      })
      .map(
        ({ id, type, actionType, tooltipTitle, onClick, disabledIndex, dataProp }): ButtonBlueprint => ({
          id,
          isIconButton: true,
          dataProp,
          size: 'medium',
          type,
          showText: false,
          onClick: () => onClick({ row }),
          actionType,
          tooltipTitle,
          toolTipPosition: 'top',
          disabled: disabledIndex ? row[disabledIndex] : false,
          translate
        })
      );
  // add blank icon where icons are hidden, to keep spacing equal in Grid column
  const missingIcon = iconButtonTypes.filter((item: any) => !actionsList.map(action => action.type).includes(item));
  const newIconButtonTypes = (iconButtonTypes: string[], missingIcon: string[]) =>
    iconButtonTypes &&
    iconButtonTypes.map((item: string) => (missingIcon && missingIcon.find((action: string) => action === item) && 'BLANK') || item);
  const getMissingId = (iconButtonTypes: string[], missingIcon: string[]) => {
    return newIconButtonTypes(iconButtonTypes, missingIcon).indexOf('BLANK') + 1;
  };
  newIconButtonTypes(iconButtonTypes, missingIcon).forEach((item: string) => {
    if (item && item === 'BLANK') {
      actionsList.push({
        id: getMissingId(iconButtonTypes, missingIcon),
        isIconButton: true,
        size: 'medium',
        type: 'BLANK',
        showText: false,
        disabled: true,
        onClick: undefined,
        translate,
        classes: blankStyle ? blankStyle : ''
      });
    }
  });
  // arraySort
  const compareId = (item1: ButtonBlueprint, item2: ButtonBlueprint) => {
    if (item1 && item2 && item1.id && item2.id && item1.id > item2.id) {
      return 1;
    }
    if (item1 && item2 && item1.id && item2.id && item1.id < item2.id) {
      return -1;
    }
    return 0;
  };
  actionsList.sort(compareId);
  actionsList.forEach(item => {
    item.id = `${item.id}$${keygen()}`;
    return item;
  });

  return (
    <LayoutGrid container justify="center">
      {actionsList.map(element => createButton(element))}
    </LayoutGrid>
  );
};

const ActionsPlugin = ({ actions, messages, translate }: ActionPluginProps) => {
  const { cellStyle, blankStyle } = useStyles();
  return (
    <Plugin name="ActionColumn" dependencies={pluginDependencies}>
      <Getter
        name="tableColumns"
        computed={({ tableColumns }) => {
          return [...tableColumns, { type: 'action', key: Symbol('Actions').toString() }];
        }}
      />
      <Template
        name="tableCell"
        predicate={({ tableColumn, tableRow }: any) => tableColumn.type === 'action' && tableRow.type === TableHeaderRow.ROW_TYPE}
      >
        <TemplateConnector>
          {() => (
            <TableCell style={{ padding: 0 }}>
              <Typography variant="h6" color="textPrimary" align="center">
                {messages.get('actionHeader')}
              </Typography>
            </TableCell>
          )}
        </TemplateConnector>
      </Template>

      <Template name="tableCell" predicate={({ tableColumn, tableRow }: any) => tableColumn.type === 'action' && tableRow.type === Table.ROW_TYPE}>
        {(params: Table.CellProps) => (
          <TemplateConnector>
            {() => {
              const { row } = params.tableRow;
              return <TableCell className={cellStyle}>{buildActionToolbar(actions as Action[], translate, row, blankStyle)}</TableCell>;
            }}
          </TemplateConnector>
        )}
      </Template>
    </Plugin>
  );
};

export { ActionsPlugin };
